// src/Pages/ExamAttempts.jsx
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Typography,
  TextField,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Backdrop,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import SideBar from "../Component/SideBar";
import { fetchTestAttempts } from "../Api/ExamsAttendedAPI";

const ExamAttempts = () => {
  const { id } = useParams();

  const [attempts, setAttempts] = useState([]);
  const [attemptSearch, setAttemptSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const isMediumScreen = useMediaQuery(theme.breakpoints.between("sm", "md"));

  const loadAttempts = async (currentPage, currentRowsPerPage) => {
    setLoading(true);
    try {
      const response = await fetchTestAttempts(
        currentPage + 1,
        currentRowsPerPage
      );

      console.log("Full API Response:", response);

      let attemptsArray = [];
      let total = 0;

      if (response && response.data) {
        if (Array.isArray(response.data.data)) {
          attemptsArray = response.data.data;
          total = response.data.total || attemptsArray.length;
        } else if (Array.isArray(response.data)) {
          attemptsArray = response.data;
          total = response.total || attemptsArray.length;
        }
      }

      setAttempts(attemptsArray);
      setTotalCount(total);
      setError(null);
    } catch (err) {
      console.error("Error loading attempts:", err);
      setError("Failed to load attempts.");
      setAttempts([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAttempts(page, rowsPerPage);
  }, [page, rowsPerPage]);

  const handleChangePage = (event, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const filteredAttempts = attempts.filter((attempt) => {
    if (!attempt) return false;
    const searchTerm = attemptSearch.toLowerCase().trim();
    if (!searchTerm) return true;
    const testName = (attempt.generalInformation?.name || "").toLowerCase();
    return testName.includes(searchTerm);
  });

  if (loading && attempts.length === 0) {
    return (
      <Box display="flex">
        <SideBar />
        <Box
          flexGrow={1}
          p={3}
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="50vh"
        >
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  return (
    <Box display="flex" flexDirection={isSmallScreen ? "column" : "row"}>
      <SideBar />
      <Box flexGrow={1} p={isSmallScreen ? 2 : 3}>
        <Typography
          variant={isSmallScreen ? "h6" : "h5"}
          gutterBottom
          sx={{ mt: isSmallScreen ? 5 : 5 }}
        >
          <b>Attended Tests</b>
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Backdrop
          open={loading}
          sx={{
            color: "#fff",
            zIndex: (theme) => theme.zIndex.drawer + 1,
          }}
        >
          <CircularProgress color="inherit" />
        </Backdrop>

        <TextField
          label="Search Test"
          variant="outlined"
          size="small"
          fullWidth
          value={attemptSearch}
          onChange={(e) => setAttemptSearch(e.target.value)}
          sx={{ mb: 2 }}
          placeholder="Search by test name"
        />

        <TableContainer component={Paper}>
          <Table size={isSmallScreen ? "small" : "medium"}>
            <TableHead sx={{ backgroundColor: "#42a5f5" }}>
              <TableRow>
                {[
                  "Test Name",
                  "Submitted On",
                  "Marks",
                  "Attempted",
                  "Correct",
                  "Incorrect",
                  "Percentage",
                ].map((header) => (
                  <TableCell
                    key={header}
                    sx={{
                      fontWeight: "bold",
                      color: "white",
                      fontSize: isSmallScreen ? "0.7rem" : "0.9rem",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {header}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredAttempts.length > 0 ? (
                filteredAttempts.map((item) => {
                  const info = item.generalInformation || {};
                  const result = item.testResult || {};

                  return (
                    <TableRow key={item.id} hover>
                      <TableCell
                        sx={{ fontSize: isSmallScreen ? "0.7rem" : "0.85rem" }}
                      >
                        {info.name || "-"}
                      </TableCell>
                      <TableCell
                        sx={{ fontSize: isSmallScreen ? "0.7rem" : "0.85rem" }}
                      >
                        {item.submittedAt
                          ? new Date(item.submittedAt).toLocaleString()
                          : "-"}
                      </TableCell>
                      <TableCell
                        sx={{ fontSize: isSmallScreen ? "0.7rem" : "0.85rem" }}
                      >
                        {result.marks || 0}
                      </TableCell>
                      <TableCell
                        sx={{ fontSize: isSmallScreen ? "0.7rem" : "0.85rem" }}
                      >
                        {result.attempted || 0}
                      </TableCell>
                      <TableCell
                        sx={{ fontSize: isSmallScreen ? "0.7rem" : "0.85rem" }}
                      >
                        {result.correct || 0}
                      </TableCell>
                      <TableCell
                        sx={{ fontSize: isSmallScreen ? "0.7rem" : "0.85rem" }}
                      >
                        {result.incorrect || 0}
                      </TableCell>
                      <TableCell
                        sx={{ fontSize: isSmallScreen ? "0.7rem" : "0.85rem" }}
                      >
                        {result.percentage || 0} %
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                    {attemptSearch
                      ? "No attempts found matching your search."
                      : "No exam attempts found."}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
            labelRowsPerPage={isSmallScreen ? "Rows" : "Rows per page"}
            sx={{
              ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows":
                {
                  fontSize: isSmallScreen ? "0.7rem" : "0.85rem",
                },
            }}
          />
        </TableContainer>
      </Box>
    </Box>
  );
};

export default ExamAttempts;
